import {
  OPENPENCIL_FIG_EDIT_PATCH_SCHEMA,
  normalizeOpenPencilFillHex,
  type OpenPencilFigEditOperation,
  type OpenPencilFigEditPatch,
  type OpenPencilFigmaSourceBundle,
} from "./openpencil-figma-source";

export interface OpenPencilSurfaceRevisionDraft {
  surfaceId: string;
  nodeId: string;
  expectedNodeType?: "FRAME" | "COMPONENT";
  expectedName: string;
  name?: string;
  visible?: boolean;
  opacity?: number;
  fillColor?: string;
  fillOpacity?: number;
  fillIndex?: number;
  width?: number;
  height?: number;
  reason: string;
}

export function buildOpenPencilSurfaceRevisionPatch(input: {
  bundle: OpenPencilFigmaSourceBundle;
  draft: OpenPencilSurfaceRevisionDraft;
  patchId?: string;
  requestedAt?: string;
}): OpenPencilFigEditPatch {
  const { bundle, draft } = input;
  if (!bundle.fileSha256) {
    throw new Error("Source revision requires a retained .fig checksum.");
  }
  if (
    (draft.width !== undefined && draft.width <= 0) ||
    (draft.height !== undefined && draft.height <= 0)
  ) {
    throw new Error("Frame width and height must be greater than zero.");
  }

  const base = {
    nodeId: draft.nodeId,
    expectedNodeType: draft.expectedNodeType ?? ("FRAME" as const),
    expectedName: draft.expectedName,
  };
  const operations: OpenPencilFigEditOperation[] = [];
  if (draft.name !== undefined) {
    operations.push({ ...base, operation: "rename-node", name: draft.name });
  }
  if (draft.visible !== undefined) {
    operations.push({
      ...base,
      operation: "set-visible",
      visible: draft.visible,
    });
  }
  if (draft.opacity !== undefined) {
    operations.push({
      ...base,
      operation: "set-opacity",
      opacity: draft.opacity,
    });
  }
  if (draft.fillColor !== undefined || draft.fillOpacity !== undefined) {
    operations.push({
      ...base,
      operation: "set-solid-fill",
      fillIndex: draft.fillIndex ?? 0,
      ...(draft.fillColor !== undefined
        ? { color: normalizeOpenPencilFillHex(draft.fillColor) }
        : {}),
      ...(draft.fillOpacity !== undefined
        ? { fillOpacity: draft.fillOpacity }
        : {}),
    });
  }
  if (draft.width !== undefined || draft.height !== undefined) {
    operations.push({
      ...base,
      operation: "set-geometry",
      ...(draft.width !== undefined ? { width: draft.width } : {}),
      ...(draft.height !== undefined ? { height: draft.height } : {}),
    });
  }
  if (operations.length === 0) {
    throw new Error("Change at least one source frame property.");
  }

  return {
    schemaVersion: OPENPENCIL_FIG_EDIT_PATCH_SCHEMA,
    patchId: input.patchId ?? `patch-${crypto.randomUUID()}`,
    baseSourceId: bundle.sourceId,
    baseSha256: bundle.fileSha256,
    actor: "human",
    reason: draft.reason,
    operations,
    requestedAt: input.requestedAt ?? new Date().toISOString(),
  };
}
